import { useRef, useState, useEffect } from 'react'

interface PassportScanModalProps {
  isOpen: boolean
  onClose: () => void
  onScanComplete?: (imageDataUrl: string) => void
  onSkip?: () => void
}

function PassportScanModal({ isOpen, onClose, onScanComplete, onSkip }: PassportScanModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const [stream, setStream] = useState<MediaStream | null>(null)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)

  // 웹캠 초기화
  useEffect(() => {
    if (!isOpen) {
      // 모달이 닫히면 스트림 정리
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
        setStream(null)
      }
      setCapturedImage(null)
      return
    }

    let currentStream: MediaStream | null = null

    navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 },
        facingMode: 'environment'
      }
    })
      .then((mediaStream) => {
        currentStream = mediaStream
        setStream(mediaStream)
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream
        }
      })
      .catch((err) => {
        console.error('웹캠 접근 실패:', err)
      })

    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop())
      }
    }
  }, [isOpen])

  // 이미지 캡처 함수
  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d')
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth
        canvasRef.current.height = videoRef.current.videoHeight
        context.drawImage(videoRef.current, 0, 0)
        const imageDataUrl = canvasRef.current.toDataURL('image/jpeg')
        setCapturedImage(imageDataUrl)
      }
    }
  }

  const handleConfirm = () => {
    if (capturedImage) {
      onScanComplete?.(capturedImage)
    }
    onClose()
  }

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setCapturedImage(null)
    onClose()
  }

  const handleSkip = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setCapturedImage(null)
    onSkip?.()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[800px] max-w-[90vw] max-h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-fx-text-dark">여권/신분증 스캔</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* 카메라 영역 */}
        <div className="flex-1 p-4 overflow-hidden">
          {!capturedImage ? (
            <div className="h-[400px] bg-black rounded-lg overflow-hidden flex items-center justify-center">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="max-w-full max-h-full object-contain"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          ) : (
            <div className="h-[400px] bg-black rounded-lg overflow-hidden flex items-center justify-center">
              <img
                src={capturedImage}
                alt="스캔된 이미지"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </div>

        {/* 버튼 영역 */}
        <div className="p-4 border-t border-gray-200 flex gap-3">
          {!capturedImage ? (
            <>
              <button
                onClick={handleSkip}
                className="flex-1 h-14 text-lg font-bold bg-gray-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
              >
                SKIP
              </button>
              <button
                onClick={captureImage}
                className="flex-1 h-14 text-lg font-bold bg-blue-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
              >
                스캔
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setCapturedImage(null)}
                className="flex-1 h-14 text-lg font-bold bg-gray-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
              >
                다시 스캔
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 h-14 text-lg font-bold bg-blue-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
              >
                확인
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PassportScanModal
